import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Loader2, Building2 } from "lucide-react";

// Admin panel for /api/admin/volume-laptop-requests. Mirrors the visual
// language of the SoftwareRequestsPanel / ServiceQuoteRequestsPanel —
// newest-first list with expandable rows + status update dropdown.

const STATUS_OPTIONS = ["new", "contacted", "closed"];

const INDUSTRY_LABELS = {
  business: "Business & Corporate",
  education: "Education",
  government: "Government & Public Sector",
  hospitality: "Hospitality",
  healthcare: "Healthcare",
  creative: "Agency / Creative Team",
  other: "Other Organization",
};
const PLATFORM_LABELS = {
  windows: "Windows",
  mac: "Mac",
  mixed: "Mix of Windows & Mac",
  not_sure: "Not sure — Recommend for us",
};
const INTENDED_USE_LABELS = {
  office: "Office / Business",
  design: "Graphic Design / Marketing",
  architecture: "Architecture / Engineering / 3D",
  mixed: "Mixed Team Requirements",
  other: "Other",
};
const SETUP_LEVEL_LABELS = {
  supply_only: "Laptop Supply Only",
  supply_plus_software: "Laptops + Software",
  ready_to_work: "Ready-to-Work Setup",
  not_sure: "Not Sure — Recommend the Best Setup",
  // Legacy pre-refinement values — kept so any historical rows still
  // display a friendly label instead of a raw enum key.
  hardware_only: "Laptop Supply Only",
  hardware_software: "Laptops + Software",
  hardware_config: "Laptops + Configuration",
  complete: "Ready-to-Work Setup",
};

export const VolumeLaptopRequestsPanel = () => {
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/admin/volume-laptop-requests");
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/volume-laptop-requests/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-[#A3A3A3] py-8 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading requests…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="ur-card p-10 text-center text-sm text-[#A3A3A3]">
        No volume laptop requests yet.
      </div>
    );
  }

  return (
    <div data-testid="volume-laptop-requests-panel" className="space-y-3">
      {rows.map((r) => {
        const open = expanded === r.id;
        const industryLabel = INDUSTRY_LABELS[r.industry] || r.industry || "—";
        const industryExtra = r.industry === "other" && r.industry_other
          ? ` · ${r.industry_other}` : "";
        const intendedLabel = INTENDED_USE_LABELS[r.intended_use] || r.intended_use || "—";
        const intendedExtra = r.intended_use === "other" && r.intended_use_other
          ? ` · ${r.intended_use_other}` : "";
        return (
          <div
            key={r.id}
            data-testid={`volume-laptop-row-${r.id}`}
            className="ur-card p-5"
          >
            <button
              type="button"
              onClick={() => setExpanded(open ? null : r.id)}
              className="w-full flex items-center gap-3 text-left"
            >
              {open ? (
                <ChevronDown className="w-4 h-4 text-[#FF6600]" />
              ) : (
                <ChevronRight className="w-4 h-4 text-[#A3A3A3]" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <div className="font-display font-bold inline-flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-[#FF6600]" strokeWidth={2} />
                    {r.organization_name}
                  </div>
                  <div className="text-xs text-[#A3A3A3]">
                    {r.contact?.name || "—"} · {r.contact?.email || "—"}
                  </div>
                </div>
                <div className="text-[11px] text-[#7A7A7A] mt-1">
                  {industryLabel}{industryExtra} · ~{r.quantity} laptops · {PLATFORM_LABELS[r.platform] || r.platform} · {new Date(r.created_at).toLocaleString()}
                </div>
              </div>
              <span
                className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${
                  r.status === "new"
                    ? "bg-[#FF6600]/20 text-[#FF6600]"
                    : r.status === "contacted"
                      ? "bg-emerald-500/15 text-emerald-400"
                      : "bg-white/10 text-[#A3A3A3]"
                }`}
              >
                {r.status}
              </span>
            </button>

            {open && (
              <div className="mt-4 pt-4 border-t border-[#262626] grid md:grid-cols-2 gap-6 text-sm">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#7A7A7A]">Organization</div>
                  <div className="mt-2 space-y-1">
                    <Row label="Name" value={r.organization_name} />
                    <Row label="Industry" value={`${industryLabel}${industryExtra}`} />
                    <Row label="Quantity" value={`~${r.quantity} laptops`} />
                    <Row label="Platform" value={PLATFORM_LABELS[r.platform] || r.platform} />
                    <Row label="Timing" value={r.timing} />
                  </div>
                  <div className="mt-4 text-[10px] font-bold uppercase tracking-widest text-[#7A7A7A]">Intended Use</div>
                  <p className="mt-2 text-[#D1D1D1]">{intendedLabel}{intendedExtra}</p>
                  <div className="mt-4 text-[10px] font-bold uppercase tracking-widest text-[#7A7A7A]">Setup Level</div>
                  <p className="mt-2 text-[#D1D1D1]">{SETUP_LEVEL_LABELS[r.setup_level] || r.setup_level || "—"}</p>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#7A7A7A]">Software Needs</div>
                  <p className="mt-2 text-[#D1D1D1] whitespace-pre-wrap">{r.software_needs || "—"}</p>
                  <div className="mt-4 text-[10px] font-bold uppercase tracking-widest text-[#7A7A7A]">Additional Info</div>
                  <p className="mt-2 text-[#D1D1D1] whitespace-pre-wrap">{r.additional_info || "—"}</p>
                  <div className="mt-4 text-[10px] font-bold uppercase tracking-widest text-[#7A7A7A]">Contact</div>
                  <div className="mt-2 space-y-1">
                    <Row label="Name" value={r.contact?.name} />
                    <Row label="Email" value={r.contact?.email} />
                    <Row label="Phone" value={r.contact?.phone} />
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-xs text-[#A3A3A3]">Status:</span>
                    <select
                      data-testid={`volume-laptop-status-${r.id}`}
                      value={r.status}
                      onChange={(e) => updateStatus(r.id, e.target.value)}
                      className="bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[#FF6600]"
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-baseline gap-2 text-xs">
    <span className="text-[#7A7A7A] w-24 shrink-0">{label}</span>
    <span className="text-[#D1D1D1]">{value || "—"}</span>
  </div>
);
